import React, { useEffect } from 'react';
import { ImageBackground, StyleSheet } from 'react-native';
import { DNABox, DNADivider, DNAText, Iffy, Stack, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider.proxy';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider.proxy';
import SlideRollWrapper from 'src/screens/Meetings/SharedComponents/SlideRollWrapper';
import PlayerWrapper from 'src/components/Presentation/PlayerWrapper';
import TextSearchPanel from 'src/components/TextSearchPanel/TextSearchPanel';
import { DNALoaderEvents } from 'src/components/DNA/Loader/DNALoader';
import AllNotes from 'src/screens/Meetings/SharedComponents/Notes/AllNotes';
import Header from './Header';
import VirtualActionBar from './VirtualActionBar';

/** ANIMATION IMPORTS */
import LottieAnimation from 'react-lottie-player';
import { isWebHtmlOrVideoDocumentVersionORM } from 'src/types/typeguards';
import HelpInstructionsModal from './HelpInstructions';
const PopupHiddenAnimation = require('src/assets/lottie/shared-window-visibility-animation.json')

const S = StyleSheet.create({
  contentContainer: {
    backgroundColor: colors['color-gray-800'],
  },
  iframeContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  iframeContent: {
    borderWidth: 4,
  },
  mainPlayerBorder: {
    minWidth: 300,
    borderColor: colors['color-success-500'],
  },
  slidebarContainer: {
    borderRightWidth: 1,
    borderColor: '#383838',
    display: 'none',
  },
  slidebarVisible: {
    display: 'flex',
  },
  contentStack: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  hiddenStack: {
    width: 0,
    height: 0,
  },
  sidebarStackLayer: {
    height: '100%',
  },
  backgroundImage: {
    flex: 1,
  },
  overlayLayer: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
});

const VirtualContent = () => {
  const { activePresentation, isCustomDeck } = useContent();
  const {
    meetingORM,
    slideRollVisible,
    setSlideRollVisible,
    toggleSlideRollVisibility,
    textSearchVisible,
    setTextSearchVisible,
    popoutContentHidden,
    openNotes,
    isMainPlayer,
  } = useMeetingsState();

  useEffect(() => {
    const isWebHtmlOrVideo = isWebHtmlOrVideoDocumentVersionORM(activePresentation?.presentable?.orm)
    if (isWebHtmlOrVideo && slideRollVisible) setSlideRollVisible(false)
  }, [activePresentation])

  if (!meetingORM) return null;

  return (
    <DNABox fill appearance="col" style={S.contentContainer}>
      <HelpInstructionsModal />
      {/* HEADER */}
      <Header />
      <DNADivider colorMode="dark" />
      {/* CONTENT */}
      <DNABox fill testID="presentation-content">
        {/* LEFT SIDEBAR */}
        <Iffy is={activePresentation && isMainPlayer}>
          {/* SLIDE ROLL */}
          <DNABox
            style={util.mergeStyles(
              undefined,
              S.slidebarContainer,
              [S.slidebarVisible, slideRollVisible],
            )}
          >
            <SlideRollWrapper closeButtonOnPress={toggleSlideRollVisibility}/>
          </DNABox>
          {/* TEXT SEARCH */}
          <DNABox
            style={util.mergeStyles(
              undefined,
              S.slidebarContainer,
              [S.slidebarVisible, textSearchVisible],
            )}
          >
            <TextSearchPanel
              onClose={() => { setTextSearchVisible(false) }}
              contentHookVariant="proxied"
            />
          </DNABox>
        </Iffy>
        <Stack anchor="bottom" style={S.contentStack}>
          <Stack.Layer style={S.contentStack}>
            <DNABox fill style={S.iframeContainer}>
              <DNABox
                fill
                style={[
                  S.iframeContent,
                  isMainPlayer ? S.mainPlayerBorder : {},
                ]}
              >
                {/* BACKGROUND IMAGE */}
                <Iffy is={!activePresentation && isMainPlayer}>
                  <ImageBackground
                    source={require('../../../../assets/images/beacon-presentation-bg.jpg')}
                    style={S.backgroundImage}
                  />
                </Iffy>
                {/* IFRAME */}
                <Iffy is={activePresentation || !isMainPlayer}>
                  <PlayerWrapper.Highlighter
                    isFullWindow
                    meetingId={meetingORM.model.id}
                    mode={isMainPlayer ? 'INTERACTIVE' : 'NON_INTERACTIVE'}
                    isFromMeetings={true}
                    highlighterVisible
                    analyticsEventType={isMainPlayer ? DNALoaderEvents.MEETING : undefined}
                    isCustomDeck={isCustomDeck}
                    useSubtleLoadingIndicator
                  />
                </Iffy>
              </DNABox>
              {/* SPEAKER/MY NOTES */}
              <AllNotes openNotes={openNotes}/>
            </DNABox>
          </Stack.Layer>
          {/* CONTENT NOT VISIBLE OVERLAY */}
          <Stack.Layer style={popoutContentHidden ? S.contentStack : S.hiddenStack}>
            <Iffy is={popoutContentHidden}>
              <DNABox
                fill
                style={S.overlayLayer}
                alignX="center"
                alignY="center"
                appearance="col"
                spacing="lg"
              >
                <DNAText h1 bold colorMode="dark">Adjust windows so shared window is visible</DNAText>
                <LottieAnimation
                  play
                  animationData={PopupHiddenAnimation}
                />
              </DNABox>
            </Iffy>
          </Stack.Layer>
        </Stack>
      </DNABox>
      {/* ACTION BAR */}
      <VirtualActionBar />
    </DNABox>
  );
};

VirtualContent.displayName = 'VirtualContent';

export default VirtualContent;
